import { ClipboardEvent, KeyboardEvent, useRef, useState } from 'react'
import { X } from 'lucide-react'

import styles from './EmailChipInput.module.scss'

import { c } from '@/utils'
import { hasAngleAddrMailbox, isValidChipEmail, normalizeEmailInput, parseEmailsWithValidation } from '@/utils/email'

interface EmailChipInputProps {
  emails: string[]
  onEmailsChange: (emails: string[]) => void
  draft: string
  onDraftChange: (text: string) => void
  validateEmail?: (email: string) => string | null
  placeholder?: string
  isDisabled?: boolean
  className?: string
}

const buildPasteError = (invalid: string[], blocked: Array<{ email: string; reason: string }>): string | null => {
  const parts: string[] = []
  if (invalid.length > 0) {
    parts.push(`${invalid.length} ${invalid.length > 1 ? 'entries' : 'entry'} couldn't be read as an email`)
  }
  const byReason = new Map<string, string[]>()
  for (const { email, reason } of blocked) {
    byReason.set(reason, [...(byReason.get(reason) ?? []), email])
  }
  byReason.forEach((blockedEmails, reason) => {
    parts.push(`${reason}: ${blockedEmails.join(', ')}`)
  })
  return parts.length > 0 ? parts.join('. ') : null
}

const EmailChipInput = ({
  emails,
  onEmailsChange,
  draft,
  onDraftChange,
  validateEmail,
  placeholder,
  isDisabled,
  className,
}: EmailChipInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)

  const tryCommitDraft = () => {
    const email = normalizeEmailInput(draft)
    if (!email) return
    if (!isValidChipEmail(email)) {
      setError('Enter a valid email address')
      return
    }
    if (emails.includes(email)) {
      setError(null)
      onDraftChange('')
      return
    }
    const validationError = validateEmail?.(email) ?? null
    if (validationError) {
      setError(validationError)
      return
    }
    setError(null)
    onEmailsChange([...emails, email])
    onDraftChange('')
  }

  const handleDraftChange = (value: string) => {
    setError(null)
    onDraftChange(value)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      tryCommitDraft()
    } else if (e.key === ',' || e.key === ' ') {
      if (isValidChipEmail(normalizeEmailInput(draft))) {
        e.preventDefault()
        tryCommitDraft()
      } else if (e.key === ' ' && !draft) {
        e.preventDefault()
      }
    } else if (e.key === 'Backspace' && !draft && emails.length > 0) {
      onEmailsChange(emails.slice(0, -1))
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text')
    const { valid, invalid } = parseEmailsWithValidation(text)
    // Single plain tokens fall through to native insertion; valid.length > 1
    // catches lists separated by whitespace alone.
    const isListPaste = /[,;\n]/.test(text) || hasAngleAddrMailbox(text) || valid.length > 1
    if (!isListPaste) return
    e.preventDefault()
    const added: string[] = []
    const blocked: Array<{ email: string; reason: string }> = []
    for (const email of valid) {
      if (emails.includes(email) || added.includes(email)) continue
      const reason = validateEmail?.(email) ?? null
      if (reason) {
        blocked.push({ email, reason })
        continue
      }
      added.push(email)
    }
    if (added.length > 0) {
      onEmailsChange([...emails, ...added])
    }
    onDraftChange([draft.trim(), ...invalid].filter((token) => token.length > 0).join(', '))
    setError(buildPasteError(invalid, blocked))
  }

  const removeEmail = (email: string) => {
    onEmailsChange(emails.filter((e) => e !== email))
    inputRef.current?.focus()
  }

  return (
    <div className={c(styles.wrapper, className)}>
      <div
        className={c(styles.container, isDisabled && styles.disabled)}
        onClick={() => inputRef.current?.focus()}
        data-testid="email-chip-input"
      >
        {emails.map((email) => (
          <span key={email} className={styles.chip} data-testid="email-chip">
            {email}
            <button
              type="button"
              className={styles.chipRemove}
              onClick={(e) => {
                e.stopPropagation()
                removeEmail(email)
              }}
              disabled={isDisabled}
              aria-label={`Remove ${email}`}
            >
              <X size={14} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          className={styles.input}
          value={draft}
          onChange={(e) => handleDraftChange(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={tryCommitDraft}
          placeholder={emails.length === 0 ? placeholder : undefined}
          disabled={isDisabled}
          type="text"
          aria-label={placeholder}
        />
      </div>
      {error && (
        <span className={styles.error} data-testid="email-chip-input-error">
          {error}
        </span>
      )}
    </div>
  )
}

export default EmailChipInput
